import React, { useCallback, useEffect, useState } from 'react';
import { legalAPI } from '../../../services/api';
import { getApiErrorMessage } from '../../../shared/utils/apiError';
import { renderLegalMarkdown } from '../utils/renderLegalMarkdown';
import { openExternal } from '../utils/openExternal';
import '../styles/legal.css';
import { useTranslation } from 'react-i18next';

/**
 * Full-screen reader of one legal document version (text from GET /v1/legal/documents/{id}).
 * Opens above the consent window and the settings sheet.
 */
const LegalDocumentSheet = ({ documentId, title, onClose }) => {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDoc(await legalAPI.getDocument(documentId));
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  }, [documentId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="legal-doc-overlay" role="dialog" aria-modal="true" aria-label={doc?.title || title}>
      <div className="legal-doc">
        <div className="legal-doc__header">
          <h2 className="legal-doc__title">{doc?.title || title}</h2>
          <button type="button" className="legal-doc__close" onClick={onClose} aria-label={t('common.close', 'Close')}>
            <img src="/close.png" alt="" />
          </button>
        </div>

        <div className="legal-doc__body">
          {error && (
            <div className="legal-doc__state">
              <p>{error}</p>
              <button type="button" className="app-btn app-btn--purple app-btn--sm" onClick={load}>{t('common.retry', 'Retry')}</button>
            </div>
          )}
          {!error && !doc && <div className="legal-doc__state"><p>{t('common.loading', 'Loading…')}</p></div>}
          {doc && (
            <>
              <p className="legal-doc__version">{t('legal.version', 'Version {{n}}', { n: doc.version })}</p>
              {renderLegalMarkdown(doc.content)}
              {doc.url && (
                <button type="button" className="legal-doc__external" onClick={() => openExternal(doc.url)}>
                  {t('legal.open_full', 'Open full version')}
                </button>
              )}
            </>
          )}
        </div>

        <div className="legal-doc__footer">
          <button type="button" className="app-btn app-btn--purple" onClick={onClose}>{t('common.back', 'Back')}</button>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentSheet;
